/**
 * Feature flags context for controlling feature visibility.
 *
 * Fetches feature flags from the API and provides them to the app.
 * Features hidden by default can be shown by enabling their flag.
 */

import { createContext, useContext, ReactNode, useState, useEffect } from 'react'
import { fetchFeatureFlags, FeatureFlags } from '@/lib/api/settings'

interface FeatureFlagsContextType {
  flags: FeatureFlags
  loading: boolean
  error: string | null
  isEnabled: (flag: keyof FeatureFlags) => boolean
}

const defaultFlags: FeatureFlags = {
  response_automation: false,
  review_request_automation: false,
  trends_page: false,
  admin_tools: false,
}

const FeatureFlagsContext = createContext<FeatureFlagsContextType | undefined>(undefined)

export function FeatureFlagsProvider({ children }: { children: ReactNode }) {
  const [flags, setFlags] = useState<FeatureFlags>(defaultFlags)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false

    fetchFeatureFlags()
      .then((data) => {
        if (!cancelled) {
          setFlags(data)
          setLoading(false)
        }
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('[FeatureFlags] Failed to fetch flags:', err)
          setError(err instanceof Error ? err.message : 'Failed to load feature flags')
          setLoading(false)
          // Keep default flags on error
        }
      })

    return () => {
      cancelled = true
    }
  }, [])

  const isEnabled = (flag: keyof FeatureFlags): boolean => flags[flag]

  return (
    <FeatureFlagsContext.Provider value={{ flags, loading, error, isEnabled }}>
      {children}
    </FeatureFlagsContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export function useFeatureFlags() {
  const context = useContext(FeatureFlagsContext)
  if (!context) {
    throw new Error('useFeatureFlags must be used within FeatureFlagsProvider')
  }
  return context
}
